// Import necessary libraries and components
import { Badge, Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
    getHL7EventDetails,
    getHL7EventLoading,
    getSelectedLog,
    getHasHl7LogAccess,
} from '../../pages/slices/hl7Log.slice'
import { formatCode, formatConnectionCode, parseJSONSafely } from '../../utils'
import { CodeBlock } from '../common/CodeBlock/CodeBlock'
import Tabs from '../common/Tabs/Tabs'
import TokenizedHl7 from './TokenizedHl7'
import ConnectionCodeHistoryTable from './ConnectionCodeHistoryTable'
import { formatTimeRemaining } from '../../pages/HL7historyLogs/utils'
import DicomAuditLogTable from './DicomAuditLogTable'
import { getDicomLogsLengthByMrn } from '../../pages/slices/dicomLog.slice'
import AccessRestrictedMessage from './AccessRestricted'
import { useDicomSenderLogs } from '../../hooks/useDicomSenderLogs'

const connectionStatusMap = {
  pending: 'Unused (Ready to Redeem)',
  connected: 'Redeemed',
  expired: 'Expired',
  disconnected: 'Expired',
}

const HL7EventDetailTabs = ({ logId, mrn }) => {
  // Selectors for Redux state
  const hl7EventLog = useSelector((state) => getSelectedLog(state, logId))
  const isHL7EventLoading = useSelector(getHL7EventLoading)
  const hl7EventDetails = useSelector(getHL7EventDetails)
  const hasHl7LogAccess = useSelector(getHasHl7LogAccess)

  const dicomLogsCountByMrn = useSelector((state) => getDicomLogsLengthByMrn(state, mrn))

  const parsedConnectionDetails = useMemo(() => {
    return parseJSONSafely(hl7EventDetails?.connectionDetails) || {}
  }, [hl7EventDetails?.connectionDetails])

  const isGoKardiaResponseIsNull = hl7EventLog?.gokardiaJsonResponse === null
  const isHl7RequestBodyIsNull = hl7EventLog?.hl7RequestBody === null

  // Parse GoKardia JSON response safely
  const goKardiaResponse = isGoKardiaResponseIsNull
    ? hasHl7LogAccess
      ? hl7EventDetails?.gokardiaJsonResponse
      : null
    : hl7EventLog?.gokardiaJsonResponse

  const getHl7bodyNoDataMessage = useCallback(() => {
    if (!hl7EventDetails?.hl7RequestBody) return 'No HL7 Request Body Available'
    return hl7EventLog?.hl7RequestBody
  }, [hl7EventDetails?.hl7RequestBody, hl7EventLog?.hl7RequestBody])

  const getGoKardiaNoDataMessage = useCallback(() => {
    if (!hl7EventDetails?.gokardiaJsonResponse)
      return 'No GoKardia JSON Response Available'
    return hl7EventLog?.gokardiaJsonResponse
  }, [hl7EventDetails?.gokardiaJsonResponse, hl7EventLog?.gokardiaJsonResponse])

  const enrollments = useMemo(() => {
    return (parsedConnectionDetails?.enrollments || []).map(
      (enrollment, index) => ({
        ...enrollment,
        id: index,
        code: formatConnectionCode(enrollment.code) || 'N/A',
        startDate: enrollment.startDate,
        endDate: enrollment.endDate,
        connectionStatus:
          connectionStatusMap[enrollment.connectionStatus] ||
          enrollment.connectionStatus ||
          'N/A',
        timeToExpire:
          enrollment.endDate && dayjs(enrollment.endDate).isAfter(dayjs())
            ? formatTimeRemaining(
                dayjs(new Date()),
                dayjs(enrollment.endDate)
              ) || 'N/A'
            : '-',
        duration: enrollment.duration || 'N/A',
      })
    )
  }, [parsedConnectionDetails?.enrollments])

    const dicomSenderLogs = useDicomSenderLogs(parsedConnectionDetails?.customParticipantID)

  // Configuration for tabs
  const tabsConfig = useMemo(
    () => [
      {
        id: 0,
        label: 'HL7 Request Body',
        content: (
          <>
            <CodeBlock
              formatter={formatCode}
              noDataMessage={getHl7bodyNoDataMessage()}
              errorMessage={!hasHl7LogAccess ? <AccessRestrictedMessage /> : ''}
            >
              {isHl7RequestBodyIsNull
                ? hl7EventDetails?.hl7RequestBody
                : hl7EventLog?.hl7RequestBody}
            </CodeBlock>
            <TokenizedHl7
              hl7Message={(isHl7RequestBodyIsNull
                ? hl7EventDetails?.hl7RequestBody
                : hl7EventLog?.hl7RequestBody
              )
                ?.split('\r')
                ?.join('\n')}
            />
          </>
        ),
      },
      {
        id: 1,
        label: 'HL7 Response Body',
        content: (
          <>
            <CodeBlock
              formatter={formatCode}
              noDataMessage={
                hl7EventLog?.hl7ResponseBody
                  ? ''
                  : 'No HL7 Response Body Available'
              }
            >
              {hl7EventLog?.hl7ResponseBody || hl7EventDetails?.hl7ResponseBody}
            </CodeBlock>
            <TokenizedHl7
              hl7Message={(
                hl7EventLog?.hl7ResponseBody || hl7EventDetails?.hl7ResponseBody
              )
                ?.split('\r')
                ?.join('\n')}
            />
          </>
        ),
      },
      {
        id: 2,
        label: 'GoKardia JSON Response',
        content: (
          <CodeBlock
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
            noDataMessage={getGoKardiaNoDataMessage()}
            errorMessage={!hasHl7LogAccess ? <AccessRestrictedMessage /> : ''}
            isJSON
          >
            {JSON.stringify(parseJSONSafely(goKardiaResponse), null, 2)}
          </CodeBlock>
        ),
      },
      {
        id: 3,
        label: (
          <Typography
            color={!!hl7EventLog?.mirthError ? 'error' : 'text.primary'}
          >
            Mirth Error
          </Typography>
        ),
        content: (
          <CodeBlock noDataMessage="No Mirth Error Available">
            {hl7EventLog?.mirthError}
          </CodeBlock>
        ),
      },
      {
        id: 4,
        // TODO: update the config option to take label and badgeCount separately
        label: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Code Redemption History by MRN
            <Badge
              badgeContent={parsedConnectionDetails?.enrollments?.length || 0}
              color="primary"
              sx={{ ml: 1 }}
            />
          </Box>
        ),
        content: (
          <ConnectionCodeHistoryTable
            rows={enrollments || []}
            totalCount={parsedConnectionDetails?.enrollments?.length || 0}
            loading={isHL7EventLoading}
            noDataMessage="No Connection Code Redemption History Available"
            hasHl7LogAccess={hasHl7LogAccess}
          />
        ),
      },
      {
        id: 5,
        label: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            DICOM Audit Log by MRN
            {/* {dicomLogsCountByMrn > 0 ? ( */}
              <Badge
                data-value={dicomLogsCountByMrn}
                badgeContent={dicomLogsCountByMrn}
                color="primary"
                sx={{ ml: 1 }}
              />
            {/* ) : null} */}
          </Box>
        ),
        content: (
          <DicomAuditLogTable
            dicomSenderLogs={dicomSenderLogs}
            hasHl7LogAccess={hasHl7LogAccess}
          />
        ),
      },
    ],
    [
      hasHl7LogAccess,
      hl7EventDetails?.hl7RequestBody,
      getGoKardiaNoDataMessage,
      getHl7bodyNoDataMessage,
      hl7EventDetails?.hl7ResponseBody,
      hl7EventLog?.hl7RequestBody,
      hl7EventLog?.hl7ResponseBody,
      hl7EventLog?.mirthError,
      goKardiaResponse,
      isHl7RequestBodyIsNull,
      isHL7EventLoading,
      parsedConnectionDetails?.enrollments,
      enrollments,
      dicomLogsCountByMrn,
      dicomSenderLogs
    ]
  )


  return <Tabs tabsConfig={tabsConfig} />
}

export default HL7EventDetailTabs
